import { inject, injectable } from 'inversify';
import Debug from 'debug';
import { DslRuntime, BooleanType, DslRuntimeSymbol } from '@xlearn/common';
import { BadgeStoreModule, BadgeStoreModuleSymbol } from '../../store';
import { BadgesEarnedNotifier } from './badges-earned-notifier';

const debug = Debug('calculate-badges-command');

@injectable()
export class CalculateBadgesCommand {

  constructor(
    @inject(BadgeStoreModuleSymbol)
    private store: BadgeStoreModule,
    @inject(BadgesEarnedNotifier)
    private badgesEarnedNotifier: BadgesEarnedNotifier,
    @inject(DslRuntimeSymbol)
    private dslRuntime: DslRuntime) {
  }

  public async execute() {

    debug('calculating badges state');

    const earnedBadgeIds: string[] = [];
    const notEarnedBadgeIds: string[] = [];

    for (const badge of this.store.badgeDescriptors) {
      debug(`executing formula`, badge.formulaSource);

      const formulaResult = this.dslRuntime.execute(badge.formula);
      if (!formulaResult.success) {
        throw new Error(`Coding error: Couldn't execute formula, error: ${JSON.stringify(formulaResult.error)}`);
      }

      debug(`formula result`, formulaResult.value);
      if (formulaResult.value.type !== BooleanType) {
        throw new Error(`Coding error: Formula result's type should be boolean, result: ${formulaResult.value}`);
      }

      const isEarned = formulaResult.value.value as boolean;
      if (isEarned) {
        earnedBadgeIds.push(badge.id);
      } else {
        notEarnedBadgeIds.push(badge.id);
      }
    }

    debug(`calculation result - earned badges`, earnedBadgeIds);
    debug(`calculation result - not earned badges`, notEarnedBadgeIds);

    this.store.setAsNotEarned(notEarnedBadgeIds);

    const localDiff = earnedBadgeIds.filter((id) => !this.store.isLocallyEarned(id));
    debug(`local diff`, localDiff);

    if (localDiff.length === 0) {
      return;
    }

    const now = new Date();
    this.store.setAsEarned({ ids: localDiff, date: now });

    debug(`remote loaded`, this.store.isRemoteLoaded);

    if (!this.store.isRemoteLoaded) {
      // do not show new badges while remote badge list not loaded
      return;
    }

    const remoteDiff = localDiff.filter((id) => !this.store.isRemotelyEarned(id));
    debug(`remote diff`, remoteDiff);

    if (remoteDiff.length === 0) {
      return;
    }

    this.badgesEarnedNotifier.notify(remoteDiff.map((id) => ({ id, date: now })));
  }
}
